/** Admin tab: the points report (whole school year or one month) and the Parent Ambassador of the Month. */
import { api } from "../api";
import { S, on, render } from "../state";
import { $, esc, toast } from "../ui";
import { showPaomThanks } from "../seasoncard";

type Report = {
  schoolYear: number;
  month: string | null;
  people: { userId: number; first: string; last: string; role: string; points: number; shadows: number }[];
  months: { month: string; label: string; points: number }[];
};
type Paom = {
  schoolYear: number;
  thisMonth: string;
  months: { month: string; label: string }[];
  picks: { month: string; label: string; userId: number; first: string; last: string }[];
  candidates: { id: number; first: string; last: string }[];
};

const PT: { report: Report | null; paom: Paom | null; month: string; loading: boolean } = { report: null, paom: null, month: "", loading: false };

export const resetPoints = () => {
  PT.report = null;
  PT.paom = null;
};

async function load() {
  if (PT.loading) return;
  PT.loading = true;
  try {
    const [r, p] = await Promise.all([api<Report>(`/api/points${PT.month ? `?month=${PT.month}` : ""}`), api<Paom>("/api/points/paom")]);
    PT.report = r;
    PT.paom = p;
  } catch (e) {
    toast((e as Error).message);
  }
  PT.loading = false;
  const box = $("#ptab");
  if (box) box.innerHTML = body();
}

const hbar = (label: string, v: number, max: number, sa = false, w = 140) =>
  `<div class="hbar"><span class="lab" style="width:${w}px" title="${esc(label)}">${esc(label)}</span><div class="bar"><i style="width:${(v / max) * 100}%;${sa ? "background:var(--accent)" : ""}"></i></div><span class="v">${v || ""}</span></div>`;

function reportCard(r: Report) {
  const max = Math.max(1, ...r.people.map((p) => p.points));
  const monthOpts = r.months.map((m) => `<option value="${m.month}" ${PT.month === m.month ? "selected" : ""}>${m.label}</option>`).join("");
  return `<div class="card"><div class="row between wrap" style="gap:8px"><h2>Points</h2><div class="row" style="gap:8px">
      <select class="f" style="width:auto" data-change="pt-month" aria-label="Period"><option value="">Whole School Year</option>${monthOpts}</select>
      <button class="btn ghost sm" data-act="pt-export">Export</button></div></div>
    <p class="muted small" style="margin:4px 0 12px">One point per event someone was checked in for, shadows included (not Team Events). People excluded from points aren't shown. ${r.people.length} ambassador${r.people.length === 1 ? "" : "s"}.</p>
    ${
      r.people.length
        ? `<div class="stack" style="gap:8px">${r.people.map((p) => hbar(`${p.first} ${p.last}`, p.points, max, p.role === "SA")).join("")}</div>
      <p class="muted small" style="margin:12px 0 0"><span style="color:var(--primary)">■</span> PA <span style="color:var(--accent);margin-left:8px">■</span> SA</p>`
        : '<p class="muted">No check-ins in this period yet.</p>'
    }</div>`;
}

function paomCard(p: Paom, r: Report) {
  const cur = p.picks.find((x) => x.month === p.thisMonth);
  // Month choices: this school year up to next month, newest first is natural here (dates keep their order).
  const months = p.months.filter((m) => m.month <= nextMonth(p.thisMonth));
  const sel = PT.month && months.some((m) => m.month === PT.month) ? PT.month : p.thisMonth;
  const top = (PT.month ? r.people : []).filter((x) => x.role.includes("PA")).slice(0, 3);
  return `<div class="card"><h2>Parent Ambassador of the Month</h2><p class="muted small" style="margin:4px 0 12px">Shows on everyone's Home page, and their pin shows through the end of the 3rd month after. The winner sees a thank-you the next time they open the site.</p>
    <div class="grid g2"><div><label class="fld" for="pom">Parent Ambassador</label><select class="f" id="pom"><option value="">Pick Someone</option>${p.candidates
      .map((c) => `<option value="${c.id}" ${cur?.userId === c.id ? "selected" : ""}>${esc(`${c.first} ${c.last}`)}</option>`)
      .join("")}</select></div>
    <div><label class="fld" for="pomm">Month</label><select class="f" id="pomm">${months.map((m) => `<option value="${m.month}" ${m.month === sel ? "selected" : ""}>${m.label}</option>`).join("")}</select></div></div>
    ${top.length ? `<p class="small" style="margin:10px 0 0">Most points in ${esc(r.months.find((m) => m.month === PT.month)!.label.split(" ")[0])}: <b>${top.map((t) => `${esc(`${t.first} ${t.last}`)} (${t.points})`).join(", ")}</b></p>` : ""}
    <div class="row wrap" style="gap:8px;margin-top:12px"><button class="btn sm" data-act="pom-save">Save</button><button class="btn ghost sm" data-act="pom-preview">Preview Thank-You</button></div></div>`;
}

function nextMonth(m: string) {
  const y = Number(m.slice(0, 4)), mo = Number(m.slice(5, 7));
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
}

function body() {
  if (!PT.report || !PT.paom) return '<div class="card"><p class="muted">Loading…</p></div>';
  const r = PT.report, p = PT.paom;
  const mmax = Math.max(1, ...r.months.map((m) => m.points));
  return `<div class="grid g2" style="align-items:start">${reportCard(r)}
    <div class="stack">${paomCard(p, r)}
    <div class="card"><h2>Points by Month</h2><p class="muted small" style="margin:2px 0 12px">July to June</p><div class="stack" style="gap:6px">${r.months
      .map((m) => hbar(m.label.slice(0, 3), m.points, mmax, false, 36))
      .join("")}</div></div>
    <div class="card"><h2>Past Parent Ambassadors of the Month</h2><p class="muted small" style="margin:2px 0 12px">${p.schoolYear}-${p.schoolYear + 1} School Year</p>
      <ul class="list">${
        p.picks
          .map(
            (x) => `<li class="row between" style="gap:8px"><span class="muted">${esc(x.label)}</span><span class="row" style="gap:8px"><b>${esc(`${x.first} ${x.last}`)}</b><button class="btn ghost sm" data-act="pom-del" data-month="${x.month}" aria-label="Remove ${esc(x.label)}">Remove</button></span></li>`,
          )
          .join("") || '<li class="muted">No one picked yet.</li>'
      }</ul></div></div></div>`;
}

export function pointsTab() {
  if (!PT.report || !PT.paom) void load();
  return `<div id="ptab">${body()}</div>`;
}

const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export function saveCsv(name: string, rows: unknown[][]) {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

on({
  "pt-month": (el) => {
    PT.month = (el as HTMLSelectElement).value;
    PT.report = null;
    $("#ptab")!.innerHTML = body();
    void load();
  },
  "pt-export": () => {
    const r = PT.report!;
    const label = PT.month ? r.months.find((m) => m.month === PT.month)!.label.toLowerCase().replace(" ", "-") : `${r.schoolYear}-${r.schoolYear + 1}`;
    saveCsv(`points-${label}.csv`, [["First", "Last", "Role", "Points", "As Shadow"], ...r.people.map((p) => [p.first, p.last, p.role, p.points, p.shadows])]);
  },
  "pom-save": async () => {
    const userId = Number(($("#pom") as HTMLSelectElement).value);
    const month = ($("#pomm") as HTMLSelectElement).value;
    if (!userId) return toast("Pick a Parent Ambassador first.");
    try {
      await api(`/api/points/paom/${month}`, { method: "PUT", body: { userId } });
      const who = PT.paom!.candidates.find((c) => c.id === userId)!;
      toast(`${who.first} ${who.last} is the Parent Ambassador of the Month.`);
      resetPoints();
      void load();
      // Home and pins come from /api/me.
      S.me = await api("/api/me");
      render();
    } catch (e) {
      toast((e as Error).message);
    }
  },
  "pom-preview": () => {
    const id = Number(($("#pom") as HTMLSelectElement).value);
    const who = PT.paom!.candidates.find((c) => c.id === id);
    const m = PT.paom!.months.find((x) => x.month === ($("#pomm") as HTMLSelectElement).value)!;
    showPaomThanks(m, who?.first || S.me!.user.first, false, true);
  },
  "pom-del": async (el) => {
    const b = el as HTMLButtonElement;
    if (b.dataset.sure !== "1") {
      b.dataset.sure = "1";
      b.textContent = "Tap Again to Remove";
      return;
    }
    try {
      await api(`/api/points/paom/${el.dataset.month}`, { method: "DELETE" });
      resetPoints();
      void load();
      S.me = await api("/api/me");
      render();
    } catch (e) {
      toast((e as Error).message);
    }
  },
});
